import { FilterLogicalOperator, FilterOperator } from '@/generated/graphql'
import type { ObjectModel } from '@/core/gql/gql-models-override.ts'
import { type FilterNodeModel, isGroupFilterNode } from '@/core/gql/filter-models.ts'
import type { PropertyType } from '@/object-types/gql/PropertyType.ts'

export class ObjectFilterService {
  public get filterTree(): FilterNodeModel[] {
    return this._filterTree
  }

  public set filterTree(tree: FilterNodeModel[]) {
    this._filterTree = tree
    this._rootNodes = tree.filter((n) => n.parentId == null)
  }

  private _filterTree: FilterNodeModel[] = []
  private _rootNodes: FilterNodeModel[] = []

  public matches(obj: ObjectModel): boolean {
    if (this._rootNodes.length === 0) return true
    return evaluateGroup(obj, this._rootNodes, this._filterTree, FilterLogicalOperator.And)
  }
}

function evaluateGroup(
  obj: ObjectModel,
  nodes: FilterNodeModel[],
  allNodes: FilterNodeModel[],
  logicalOp: FilterLogicalOperator,
): boolean {
  if (logicalOp === FilterLogicalOperator.Or) {
    for (const node of nodes) {
      if (evaluateNode(obj, node, allNodes)) return true
    }
    return false
  }
  for (const node of nodes) {
    if (!evaluateNode(obj, node, allNodes)) return false
  }
  return true
}

function evaluateNode(
  obj: ObjectModel,
  node: FilterNodeModel,
  allNodes: FilterNodeModel[],
): boolean {
  if (isGroupFilterNode(node)) {
    const children = allNodes.filter((n) => n.parentId === node.id)
    return evaluateGroup(obj, children, allNodes, node.logicalOperator)
  }

  if (node.propertyDefinitionId == null || node.operator == null) return true

  const rawObjValue = obj.propertyValues.get(node.propertyDefinitionId)
  if (rawObjValue == null) {
    return node.operator === FilterOperator.IsEmpty
  }

  switch (node.operator) {
    case FilterOperator.Eq:
      return rawObjValue === node.value
    case FilterOperator.Neq:
      return rawObjValue !== node.value

    case FilterOperator.Contains: {
      const haystack = (rawObjValue as string).toLowerCase()
      const needle = node.value != null ? (node.value as string).toLowerCase() : ''
      return haystack.includes(needle)
    }
    case FilterOperator.NotContains: {
      const haystack = (rawObjValue as string).toLowerCase()
      const needle = node.value != null ? (node.value as string).toLowerCase() : ''
      return !haystack.includes(needle)
    }
    case FilterOperator.StartsWith: {
      const haystack = (rawObjValue as string).toLowerCase()
      const needle = node.value != null ? (node.value as string).toLowerCase() : ''
      return haystack.startsWith(needle)
    }
    case FilterOperator.EndsWith: {
      const haystack = (rawObjValue as string).toLowerCase()
      const needle = node.value != null ? (node.value as string).toLowerCase() : ''
      return haystack.endsWith(needle)
    }

    case FilterOperator.IsEmpty:
      return rawObjValue === ''
    case FilterOperator.IsNotEmpty:
      return rawObjValue !== ''

    case FilterOperator.In: {
      if (node.values == null) return false
      if (Array.isArray(rawObjValue))
        return (rawObjValue as number[]).some((v) => node.values!.includes(v))
      return node.values.includes(rawObjValue as number)
    }
    case FilterOperator.NotIn: {
      if (node.values == null) return true
      if (Array.isArray(rawObjValue))
        return !(rawObjValue as number[]).some((v) => node.values!.includes(v))
      return !node.values.includes(rawObjValue as number)
    }
    case FilterOperator.AllIn: {
      if (node.values == null) return true
      return node.values.every((v) => (rawObjValue as number[]).includes(v))
    }
    case FilterOperator.Gt:
      return (rawObjValue as number) > (node.value as number)
    case FilterOperator.Gte:
      return (rawObjValue as number) >= (node.value as number)
    case FilterOperator.Lt:
      return (rawObjValue as number) < (node.value as number)
    case FilterOperator.Lte:
      return (rawObjValue as number) <= (node.value as number)
    default:
      return true
  }
}

export function isSupportedType(type: PropertyType): boolean {
  return type === 'text' || type === 'select-1' || type === 'multi-select'
}

export function operatorsFor(type: PropertyType): { label: string; value: FilterOperator }[] {
  switch (type) {
    case 'text':
      return [
        { label: 'Contains', value: FilterOperator.Contains },
        { label: 'Not contains', value: FilterOperator.NotContains },
      ]
    case 'select-1':
    case 'date':
    case 'toggle':
      return [
        { label: 'Is', value: FilterOperator.Eq },
        { label: 'Is not', value: FilterOperator.Neq },
      ]
    case 'multi-select':
      return [
        { label: 'Has Any', value: FilterOperator.In },
        { label: 'Has All', value: FilterOperator.AllIn },
        { label: 'Does not have any', value: FilterOperator.NotIn },
      ]
    case 'number':
      return [
        { label: 'Is', value: FilterOperator.Eq },
        { label: 'Is not', value: FilterOperator.Neq },
        { label: '>', value: FilterOperator.Gt },
        { label: '>=', value: FilterOperator.Gte },
        { label: '<', value: FilterOperator.Lt },
        { label: '<=', value: FilterOperator.Lte },
      ]

    default:
      throw new Error('not impl')
  }
}
